// components/tour/TourHost.jsx
//
// Mounted once in AppShell. Owns the tour UI driven by tourStore:
//   • phase 'menu'    → the mascot-flavored chooser ("Getting Started" vs
//                       "Tour this page" for the current route)
//   • phase 'running' → the <SpotlightTour> for the chosen track
// Role-filters + route-resolves the tracks for the signed-in user via buildTracks.

import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTourControl, tourStore } from './tourStore';
import { buildTracks, trackForPath } from './tourSteps';
import SpotlightTour from './SpotlightTour';
import './SpotlightTour.css';

const MASCOT = '/assets/kb-navigator/frame-1.png';

export default function TourHost({ staffId = null, isSuperUser = false }) {
  const control = useTourControl();
  const location = useLocation();
  const tracks = useMemo(() => buildTracks({ staffId, isSuperUser }), [staffId, isSuperUser]);

  if (control.phase === 'running' && control.trackKey) {
    const track = tracks[control.trackKey];
    if (!track || track.steps.length === 0) { tourStore.close(); return null; }
    return <SpotlightTour steps={track.steps} onClose={() => tourStore.close()} />;
  }

  if (control.phase !== 'menu') return null;

  const pageKey = trackForPath(location.pathname);
  const pageTrack = pageKey ? tracks[pageKey] : null;
  const hasPageTrack = !!pageTrack && pageTrack.steps.length > 0;

  return createPortal(
    <div
      className="tour-chooser-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) tourStore.close(); }}
    >
      <div className="tour-chooser" role="dialog" aria-label="Take a tour">
        <button className="tour-chooser-x" aria-label="Close" onClick={() => tourStore.close()}>&times;</button>
        <div className="tour-chooser-head">
          <img className="tour-mascot" src={MASCOT} alt="" />
          <div>
            <div className="tour-chooser-title">Take a tour</div>
            <div className="tour-chooser-sub">Quick guided highlights — press End anytime.</div>
          </div>
        </div>
        <div className="tour-chooser-opts">
          <button
            className="tour-chooser-opt tour-chooser-opt--primary"
            onClick={() => tourStore.start('gettingStarted')}
          >
            <span className="tour-chooser-opt-title">🚀 Getting Started</span>
            <span className="tour-chooser-opt-sub">The essentials, ~2 minutes</span>
          </button>
          {hasPageTrack && (
            <button className="tour-chooser-opt" onClick={() => tourStore.start(pageKey)}>
              <span className="tour-chooser-opt-title">📍 Tour this page</span>
              <span className="tour-chooser-opt-sub">{pageTrack.label}</span>
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
